import { useEffect, useMemo, useState } from "react";
import { styles } from "../../styles";
import { DAYS, DAYS_SHORT, MONTHS } from "../../lib/constants";
import { ymd, monthGrid, slotsForDay, minutesToLabel } from "../../lib/time";
import { useShop } from "../../context/ShopContext";
import { useToast } from "../../context/ToastContext";
import { subscribeCapacityRange, type CapacityMap } from "../../data/capacity";
import { bookAppointment } from "../../data/bookings";
import { Empty } from "../common";
import { BookingForm, type BookingDetails } from "./BookingForm";
import { Confirmation, type ConfirmedBooking } from "./Confirmation";

// Only echo the function's own validation messages, which are written for
// customers. For transport or internal errors (network, cold start, crash) the
// message is not user friendly, so we show a curated fallback instead.
const SAFE_CODES = new Set([
  "functions/failed-precondition",
  "functions/already-exists",
  "functions/resource-exhausted",
  "functions/invalid-argument",
]);
const GENERIC_BOOKING_ERROR =
  "We could not complete your booking just now. Please pick a time again, or try once more in a moment.";

function friendlyBookingError(err: unknown): string {
  const code = (err as { code?: string })?.code;
  const message = err instanceof Error ? err.message : "";
  if (code && SAFE_CODES.has(code) && message) return message;
  return GENERIC_BOOKING_ERROR;
}

export function Customer() {
  const { config, breeds, closedDates } = useShop();
  const notify = useToast();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const activeBreeds = useMemo(() => breeds.filter((b) => b.active), [breeds]);

  const [breedId, setBreedId] = useState("");
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [capacity, setCapacity] = useState<CapacityMap>({});
  const [confirmed, setConfirmed] = useState<ConfirmedBooking | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const selectedBreed = activeBreeds.find((b) => b.id === breedId) ?? null;
  const slotMinutes = selectedBreed?.appointmentMinutes ?? 0;

  const grid = useMemo(() => monthGrid(cursor), [cursor]);
  // Six weeks of seven days, so the calendar can use proper ARIA grid rows.
  const weeks = useMemo(
    () => Array.from({ length: 6 }, (_, w) => grid.slice(w * 7, w * 7 + 7)),
    [grid],
  );

  // Keep capacity in sync with the visible month so full-for-this-breed days and
  // taken slots reflect live bookings.
  useEffect(() => {
    const startKey = ymd(grid[0]);
    const endKey = ymd(grid[grid.length - 1]);
    const unsub = subscribeCapacityRange(
      startKey, endKey, setCapacity,
      (e) => console.error("Failed to load availability:", e),
    );
    return unsub;
  }, [grid]);

  const isBlocked = (d: Date) => closedDates.includes(ymd(d));
  // Past, or today when same-day booking is turned off (soonest is tomorrow).
  const isPast = (d: Date) => (config.blockSameDay ? d <= today : d < today);
  const isClosed = (d: Date) => !config.hours[d.getDay()]?.open;
  const breedFull = (d: Date): boolean => {
    if (!selectedBreed) return false;
    const count = capacity[ymd(d)]?.counts?.[selectedBreed.name] ?? 0;
    return count >= selectedBreed.dailyMax;
  };
  const dayUnavailable = (d: Date) => isPast(d) || isBlocked(d) || isClosed(d) || breedFull(d);

  const slotsFor = (date: Date) =>
    slotsForDay(config.hours[date.getDay()], slotMinutes, capacity[ymd(date)]?.busy ?? [], config.cooldownMinutes);

  const pickDate = (d: Date) => {
    if (dayUnavailable(d)) return;
    setSelectedDate(d);
    setSelectedSlot(null);
    setSubmitError(null);
  };

  const onPickBreed = (id: string) => {
    setBreedId(id);
    // Availability is breed-specific, so reset the day/slot selection.
    setSelectedDate(null);
    setSelectedSlot(null);
    setSubmitError(null);
  };

  const handleSubmit = async (details: BookingDetails) => {
    if (!selectedDate || selectedSlot === null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await bookAppointment({
        date: ymd(selectedDate),
        startMinutes: selectedSlot,
        dogName: details.dogName,
        breed: details.breed,
        ownerName: details.ownerName,
        phone: details.phone,
        ownerEmail: details.email,
      });
      setConfirmed({
        dogName: details.dogName,
        breed: details.breed,
        ownerName: details.ownerName,
        phone: details.phone,
        email: details.email,
        date: selectedDate,
        start: res.startMinutes,
        end: res.endMinutes,
        timezone: config.timezone,
        shopName: config.shopName || "Radiance",
        shopEmail: config.shopEmail,
      });
      notify("Appointment confirmed. Invite on its way.");
    } catch (err) {
      setSubmitError(friendlyBookingError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <Confirmation
        booking={confirmed}
        onDone={() => {
          setConfirmed(null);
          setSelectedDate(null);
          setSelectedSlot(null);
        }}
      />
    );
  }

  return (
    <div className="fade">
      <section style={styles.heroWrap}>
        <p style={styles.eyebrow}>Appointments</p>
        <h1 style={styles.h1}>Book a spa day for your dog.</h1>
        <p style={styles.lede}>
          Tell us the breed, pick a day, choose a time. We'll send a calendar invite to confirm.
        </p>
      </section>

      {config.adminMessage?.trim() && (
        <div role="status" style={styles.banner}>
          <span style={styles.bannerDot} aria-hidden="true" />
          <div>
            <strong style={styles.bannerTitle}>A note from the shop</strong>
            <p style={styles.bannerBody}>{config.adminMessage}</p>
          </div>
        </div>
      )}

      {/* Step 1: breed, since it sets appointment length and daily availability. */}
      <div style={{ ...styles.card, marginBottom: 20 }}>
        <h2 style={styles.cardTitle}>Which breed is coming in?</h2>
        <p style={styles.sub}>Breed sets how long the grooming takes and which days have room.</p>
        <div style={styles.breedRow}>
          <select
            value={breedId} onChange={(e) => onPickBreed(e.target.value)}
            className="input select" style={styles.breedSelect} aria-label="Dog's breed"
          >
            <option value="" disabled>Choose a breed</option>
            {activeBreeds.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        {selectedBreed && (
          <p style={styles.breedMeta}>
            {selectedBreed.name}: {selectedBreed.appointmentMinutes}-minute appointments.
          </p>
        )}
      </div>

      <div className="bookgrid" style={styles.bookGrid}>
        {/* Calendar */}
        <div style={styles.card}>
          <div style={styles.calHead}>
            <h2 style={styles.cardTitle}>{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h2>
            <div style={styles.calNav}>
              <button
                className="iconbtn" style={styles.iconBtn} aria-label="Previous month"
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              >‹</button>
              <button
                className="iconbtn" style={styles.iconBtn} aria-label="Next month"
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
              >›</button>
            </div>
          </div>
          <div style={styles.weekRow} aria-hidden="true">
            {DAYS_SHORT.map((d) => <div key={d} style={styles.weekCell}>{d}</div>)}
          </div>
          <div role="grid" aria-label="Choose a date" style={styles.dayGridRows}>
            {weeks.map((week, wi) => (
              <div role="row" key={wi} style={styles.dayGrid}>
                {week.map((d, i) => {
                  const inMonth = d.getMonth() === cursor.getMonth();
                  const off = dayUnavailable(d);
                  const sel = selectedDate != null && ymd(d) === ymd(selectedDate);
                  const isToday = ymd(d) === ymd(today);
                  const full = inMonth && !isPast(d) && !isClosed(d) && !isBlocked(d) && breedFull(d);
                  const reason = isBlocked(d) ? ", holiday" : full ? ", fully booked for this breed" : off ? ", unavailable" : "";
                  return (
                    <button
                      key={i} role="gridcell" disabled={off || !inMonth}
                      aria-label={`${DAYS[d.getDay()]} ${MONTHS[d.getMonth()]} ${d.getDate()}${reason}`}
                      aria-selected={sel}
                      className="daybtn"
                      style={{
                        ...styles.day,
                        ...(inMonth ? {} : styles.dayMuted),
                        ...(off && inMonth ? styles.dayOff : {}),
                        ...(sel ? styles.daySel : {}),
                        ...(isToday && !sel ? styles.dayToday : {}),
                      }}
                      onClick={() => pickDate(d)}
                    >
                      {d.getDate()}
                      {isBlocked(d) && inMonth && <span style={styles.blockDot} aria-hidden="true" />}
                      {full && !isBlocked(d) && <span style={styles.fullDot} aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div style={styles.legend}>
            <span style={styles.legendItem}><span style={{ ...styles.swatch, background: "var(--accent)" }} />Selected</span>
            <span style={styles.legendItem}><span style={{ ...styles.swatch, border: "1.5px solid var(--accent)", background: "transparent" }} />Today</span>
            <span style={styles.legendItem}><span style={{ ...styles.swatch, background: "var(--block)" }} />Closed</span>
            <span style={styles.legendItem}><span style={{ ...styles.swatch, background: "var(--muted)" }} />Breed full</span>
          </div>
        </div>

        {/* Times + form */}
        <div style={styles.card}>
          {!selectedBreed ? (
            <Empty title="Choose a breed to start" body="Breed decides the appointment length, so we ask for it first." />
          ) : !selectedDate ? (
            <Empty title="Pick a date to start" body="Open days are tappable. Grayed-out days are closed, holidays, or full for this breed." />
          ) : (
            <>
              <h2 style={styles.cardTitle}>
                {DAYS[selectedDate.getDay()]}, {MONTHS[selectedDate.getMonth()]} {selectedDate.getDate()}
              </h2>
              <p style={styles.sub}>Choose a {slotMinutes}-minute slot.</p>
              <div style={styles.slotWrap} role="group" aria-label="Available times">
                {slotsFor(selectedDate).length === 0 && <Empty title="No times this day" body="Try another date." />}
                {slotsFor(selectedDate).map((s) => {
                  const on = selectedSlot === s.start;
                  return (
                    <button
                      key={s.start} disabled={s.taken} className="slot" aria-pressed={on}
                      style={{ ...styles.slot, ...(s.taken ? styles.slotTaken : {}), ...(on ? styles.slotOn : {}) }}
                      onClick={() => { setSelectedSlot(s.start); setSubmitError(null); }}
                    >
                      {minutesToLabel(s.start)}
                      {s.taken && <span style={styles.takenTag}>Booked</span>}
                    </button>
                  );
                })}
              </div>

              {selectedSlot !== null && (
                <BookingForm
                  start={selectedSlot}
                  breeds={activeBreeds}
                  initialBreed={selectedBreed.name}
                  onSubmit={handleSubmit}
                  submitting={submitting}
                  submitError={submitError}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
